import type { Rental } from '../lib/rental';
import { highlight, formatDate } from '../lib/utils';
import Description from './Description';
import { formatPrice, getDisplayPrice } from '../lib/price';
import { BASE_CURRENCY } from '../lib/consts';
import useCurrencies from '../hooks/useCurrencies';

// TODO: Why aren't we passing in the highlightTerms here?
const RentalAttributes = ({ rental, expandDetails = true, highlightTerms = [], displayCurrency }: {
        rental: Rental, expandDetails: boolean, highlightTerms?: string[], displayCurrency: string
    }) => {
        const exchangeRates = useCurrencies();

        let sourceUrl = rental.source_url ?? null;
        if (rental.source === 'telegram') {
            const channel_name = rental.source_channel_name;
            const channel_id = rental.source_channel_id;

            if (channel_name && /^[a-zA-Z0-9_]+$/.test(channel_name)) {
                sourceUrl = `https://t.me/${channel_name}/${rental.message_id}`;
            } else if (channel_id) {
                let formatted_id = channel_id.toString();
                if (formatted_id.startsWith('-100')) {
                    formatted_id = formatted_id.substring(4);
                } else if (formatted_id.startsWith('-')) {
                    formatted_id = formatted_id.substring(1);
                }
                
                if (rental.topic_id) {
                    sourceUrl = `https://t.me/c/${formatted_id}/${rental.topic_id}/${rental.message_id}`;
                } else {
                    sourceUrl = `https://t.me/c/${formatted_id}/${rental.message_id}`;
                }
            } else if (channel_name) {
                sourceUrl = `https://t.me/${channel_name}/${rental.message_id}`;
            }
        }

        const description =
            <div className="mt-2 p-3 bg-gray-50 rounded-md border text-xs whitespace-pre-wrap">
                <div>
                    <p>
                        <strong>📞 Contact:</strong>{' '}
                        {(!rental.contact_name && !rental.contact_url) ? (
                            <span className="text-gray-500 italic">Contact information not available</span>
                        ) : (
                            <a href={rental.contact_url ?? sourceUrl ?? ''} target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline font-medium"
                                data-umami-event="Contact link clicked"
                            >
                                {rental.contact_name ?? rental.contact_url}
                            </a>
                        )}
                    </p>
                    <p className="col-span-2"><strong>📨 Source listing: </strong>
                        {(!sourceUrl) ? (
                            <span className="text-gray-500 italic">Source URL not available</span>
                        ) : (
                            <a
                                href={sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline hover:text-blue-800"
                                data-umami-event="Source link clicked"
                            >{rental.source_channel_name ?? rental.source}
                            </a>
                        )}
                    </p>
                </div>
                <br />
                <p>{highlight(rental.raw_description ?? '', highlightTerms)}</p>
            </div>;

        const amenities = highlight(rental.amenities?.length ? rental.amenities.join(', ') : 'N/A', highlightTerms);
        const displayPrice = getDisplayPrice(rental, exchangeRates, displayCurrency);

        const locationLink = () => (!rental.location ?
            'N/A' :
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rental.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
                data-umami-event="Location link clicked"
            >{rental.location}</a>
        );

        return (
            <div>
                <div className="text-lg text-emerald-600 font-medium mb-3">
                    {rental.price_amount
                        ? `${formatPrice(rental.price_amount, rental.price_currency || BASE_CURRENCY)} / ${rental.rental_period || ''}`
                        : 'Price: N/A'}
                    {rental.price_per_month && (rental.price_per_month != rental.price_amount || rental.price_currency != displayCurrency)
                        && displayPrice ?
                        (<div className="text-sm text-gray-600">
                            {`${formatPrice(displayPrice, displayCurrency)} / month`}
                        </div>) : ''
                    }
                </div>
                <div className="gap-x-4 text-sm text-gray-700 mb-3">
                    <p><strong>📍 Approx. Location:</strong> {locationLink()}</p>
                    <p><strong>🛏 Bedrooms:</strong> {rental.bedrooms ?? 'N/A'}</p>
                    <p><strong>🛁 Bathrooms:</strong> {rental.bathrooms ?? 'N/A'}</p>
                    <p><strong>🛎 Amenities:</strong> {amenities}</p>
                </div>

                {expandDetails ? (<details className="text-sm text-gray-600 mt-2">
                    <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">
                        📜 Show Details
                    </summary>
                    <Description>{description}</Description>
                </details>) : (<Description>{description}</Description>)
                }

                <p className="text-xs text-gray-400 mt-4 text-right">
                    📅 Posted: {formatDate(rental.posted_at_source)}
                </p>
            </div>
        );
    };

export default RentalAttributes;
